import { useCallback, useEffect, useRef, useState } from "react";
import { drApi } from "../services/drApi";
import { apiErrorMessage } from "../utils/apiError";
import { submissionRevOf } from "../utils/suitabilityRevision";

const idle = (investmentId = null) => ({
  status: "idle",
  investmentId,
  answers: [],
  submissionRev: null,
  error: null,
});

/**
 * useSuitabilityAnswers - the investor's suitability answers for one order and
 * the revision of the submission they belong to (API round 15).
 *
 * Pass null to read nothing (a KYC or classification item, or a suitability
 * item with no order). The state names the investment it was read for, so a
 * page can check it is showing the answers of the item it decides (Codex r14
 * P1). Only the newest read lands: a late answer for a previous investment,
 * or for a read a newer one replaced, changes nothing. reload() only ever
 * re-reads the CURRENT investment: it bumps a counter rather than closing
 * over an id, so a callback kept from before a change of item cannot bring
 * the old answers back.
 *
 * @returns {{ status: 'idle'|'loading'|'ready'|'error', investmentId: any,
 *             answers: Array, submissionRev: number|null, error: string|null,
 *             reload: Function }}
 */
export default function useSuitabilityAnswers(investmentId) {
  const [state, setState] = useState(() => idle());
  const [reloads, setReloads] = useState(0);
  const seq = useRef(0);

  useEffect(() => {
    const mine = ++seq.current;
    if (investmentId === null || investmentId === undefined) {
      setState(idle());
      return undefined;
    }
    setState({ status: "loading", investmentId, answers: [], submissionRev: null, error: null });
    drApi
      .suitabilityAnswers(investmentId)
      .then((body) => {
        if (mine !== seq.current) return;
        setState({
          status: "ready",
          investmentId,
          answers: Array.isArray(body?.data) ? body.data : [],
          submissionRev: submissionRevOf(body?.result),
          error: null,
        });
      })
      .catch((err) => {
        if (mine !== seq.current) return;
        setState({
          status: "error",
          investmentId,
          answers: [],
          submissionRev: null,
          error: apiErrorMessage(err, "Request failed"),
        });
      });
    return () => {
      // A new investment, a reload or an unmount retires this read.
      seq.current += 1;
    };
  }, [investmentId, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);

  return { ...state, reload };
}
